import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const DailyMedia = () => {
  const navigate = useNavigate();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/daily-media.php');
        const data = await res.json();
        if (Array.isArray(data)) setMedia(data);
        else if (data.data) setMedia(data.data);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const uniqueTypes = [...new Set(media.map(item => item.media_type).filter(Boolean))];
  const uniqueCategories = [...new Set(media.map(item => item.category).filter(Boolean))];

  const filtered = media.filter(item => {
    const t = selectedType === 'all' || item.media_type === selectedType;
    const c = selectedCategory === 'all' || item.category === selectedCategory;
    return t && c;
  });

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  return (
    <div className="page-enter">
      <Helmet>
        <title>Daily Media — PAK School &amp; College System | Photos &amp; Videos</title>
        <meta name="description" content="Explore PAK School & College System's daily media collection — photos and videos capturing school life, events, and memorable moments." />
        <link rel="canonical" href="https://pakschoolandcollege.com/daily-media" />
      </Helmet>

      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📷 School Life
          </div>
          <h1>Daily <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Media</span></h1>
          <p>Daily photos and videos showcasing the vibrant life, events, and memorable moments at PAK School</p>
        </div>
      </div>

      <div className="py-14 bg-white min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filters */}
          {media.length > 0 && (
            <div className="bg-gray-50 rounded-2xl p-5 mb-8 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Media Type</label>
                <select value={selectedType} onChange={e => setSelectedType(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white">
                  <option value="all">All Types</option>
                  {uniqueTypes.map(t => <option key={t} value={t}>{t?.charAt(0).toUpperCase() + t?.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Category</label>
                <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white">
                  <option value="all">All Categories</option>
                  {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Stats */}
          {!loading && media.length > 0 && (
            <div className="flex flex-wrap gap-4 mb-8">
              {[
                { label: 'Photos', count: media.filter(i => i.media_type === 'image').length, icon: 'fa-camera', color: 'text-primary' },
                { label: 'Videos', count: media.filter(i => i.media_type === 'video').length, icon: 'fa-video', color: 'text-accent' },
                { label: 'Categories', count: uniqueCategories.length, icon: 'fa-tags', color: 'text-green-600' },
              ].filter(s => s.count > 0).map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5">
                  <i className={`fas ${s.icon} ${s.color}`} />
                  <span className="font-poppins font-700 text-navy">{s.count}</span>
                  <span className="text-gray-400 font-inter text-sm">{s.label}</span>
                </div>
              ))}
            </div>
          )}

          {loading && <div className="flex flex-col items-center py-24 gap-4"><div className="spinner" /><p className="text-gray-400 text-sm font-inter">Loading media...</p></div>}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
                <i className="fas fa-photo-video text-3xl text-gray-300" />
              </div>
              <h3 className="font-poppins font-700 text-gray-700 text-xl mb-2">No Media Found</h3>
              <p className="text-gray-400 font-inter text-sm">Try adjusting your filters or check back soon for new content.</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((item) => (
                <div key={item.id}
                  className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/daily-media/${item.id}`)}>
                  <div className="relative h-44 overflow-hidden bg-gray-100">
                    {item.media_type === 'image' ? (
                      <img src={`admin/${item.file_path}`} alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                        <video src={`admin/${item.file_path}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <i className="fas fa-play text-navy ml-0.5" />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-2.5 left-2.5">
                      <span className={`text-xs font-poppins font-700 px-2 py-1 rounded-full ${item.media_type === 'image' ? 'bg-primary/90 text-white' : 'bg-accent/90 text-white'}`}>
                        <i className={`fas ${item.media_type === 'image' ? 'fa-camera' : 'fa-play'} mr-1 text-[9px]`} />{item.media_type}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/20 transition-all duration-300" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-poppins font-700 text-navy text-sm mb-2 line-clamp-2 group-hover:text-primary transition-colors">{item.title}</h3>
                    <div className="space-y-1">
                      {item.category && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-tag text-primary/60 w-3.5" />{item.category}</div>}
                      {item.date && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-calendar text-gray-300 w-3.5" />{fmtDate(item.date)}</div>}
                      {item.location && <div className="flex items-center gap-2 text-xs text-gray-400 font-inter"><i className="fas fa-map-marker-alt text-accent/60 w-3.5" /><span className="truncate">{item.location}</span></div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DailyMedia;
